import React, { useCallback, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  CheckCircle2, ChevronLeft, ChevronRight, LoaderCircle, LockKeyhole, RefreshCw,
  Save, ShieldCheck, UserCircle2, UserPlus, Users,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useApp } from "@/context/AppContext";
import { createAdminUser, listAdminUsers, updateAdminUser } from "@/lib/api";
import { timeAgo } from "@/lib/formatters";
import { ADMIN_ROLE_VALUES, adminInviteSchema } from "./adminUsersModel";

const PAGE_SIZE = 40;
const STATUSES = ["Invited", "Active", "Disabled"];

function statusTone(status) {
  if (status === "Active") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "Disabled") return "border-slate-200 bg-slate-100 text-slate-600";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

export default function AdminUsers() {
  const { currentAdmin } = useApp();
  const canManage = currentAdmin?.permissions?.includes("admin_users.manage");
  const [admins, setAdmins] = useState([]);
  const [metrics, setMetrics] = useState({ total: 0, active: 0, invited: 0, disabled: 0 });
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cursorStack, setCursorStack] = useState([""]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState("");

  const form = useForm({
    resolver: zodResolver(adminInviteSchema),
    defaultValues: { fullName: "", email: "", role: "SupportAgent" },
  });

  const filters = useMemo(() => ({
    cursor: cursorStack[pageIndex] || undefined,
    limit: PAGE_SIZE,
    query: query || undefined,
    role: roleFilter === "all" ? undefined : roleFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
  }), [cursorStack, pageIndex, query, roleFilter, statusFilter]);

  const loadAdmins = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const payload = await listAdminUsers(filters, signal);
      const items = Array.isArray(payload?.items) ? payload.items : payload?.admins || [];
      setAdmins(items);
      setMetrics(payload?.metrics || { total: items.length, active: 0, invited: 0, disabled: 0 });
      setTotal(Number(payload?.total ?? items.length));
      setRoles(Array.isArray(payload?.roles) ? payload.roles : []);
      setNextCursor(payload?.nextCursor || "");
      setDrafts(Object.fromEntries(items.map((item) => [
        item.id, { role: item.role, status: item.status },
      ])));
    } catch (requestError) {
      if (requestError?.name !== "AbortError") setError(requestError?.message || "Could not load admin users");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    loadAdmins(controller.signal);
    return () => controller.abort();
  }, [loadAdmins]);

  const resetPagination = () => {
    setCursorStack([""]);
    setPageIndex(0);
  };

  const submitInvite = form.handleSubmit(async (values) => {
    setInviteBusy(true);
    try {
      const payload = await createAdminUser(values);
      setInviteResult(payload);
      toast.success("Invitation created");
      form.reset();
      resetPagination();
      await loadAdmins();
    } catch (requestError) {
      if (requestError?.status === 409) form.setError("email", { message: requestError.message });
      else toast.error(requestError?.message || "Could not create admin");
    } finally {
      setInviteBusy(false);
    }
  });

  const saveRow = async (item) => {
    const draft = drafts[item.id];
    setSavingId(item.id);
    try {
      await updateAdminUser(item.id, { ...draft, version: item.version });
      toast.success("Access updated");
      await loadAdmins();
    } catch (requestError) {
      toast.error(requestError?.message || "Could not update admin");
      if (requestError?.status === 409) await loadAdmins();
    } finally {
      setSavingId("");
    }
  };

  const goNext = () => {
    if (!nextCursor) return;
    setCursorStack((previous) => [...previous.slice(0, pageIndex + 1), nextCursor]);
    setPageIndex((value) => value + 1);
  };

  const roleOptions = roles.length ? roles : ADMIN_ROLE_VALUES.map((value) => ({ value, label: value, description: "" }));
  const start = total ? pageIndex * PAGE_SIZE + 1 : 0;
  const end = total ? Math.min(start + admins.length - 1, total) : 0;

  return (
    <div className="space-y-6" data-testid="admins-page">
      <PageHeader
        eyebrow="Security"
        title="Admin users"
        subtitle="Least-privilege access, protected identities and auditable operational roles."
        actions={<>
          <Button variant="outline" onClick={() => loadAdmins()} disabled={loading}><RefreshCw className="mr-1.5 h-4 w-4" />Refresh</Button>
          {canManage && (
            <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) { form.reset(); setInviteResult(null); } }}>
              <DialogTrigger asChild><Button className="bg-[#0E5B52] hover:bg-[#0B4A43]"><UserPlus className="mr-1.5 h-4 w-4" />Invite admin</Button></DialogTrigger>
              <DialogContent className="border-[#0E5B52]/15 bg-white p-0 shadow-2xl sm:max-w-xl">
                <div className="border-b border-[#0E5B52]/10 bg-gradient-to-br from-[#F5FBF7] to-white px-6 py-5">
                  <DialogHeader>
                    <DialogTitle className="text-[#064E3B]">Invite an operational teammate</DialogTitle>
                    <DialogDescription>Assign only the access required for their day-to-day responsibilities.</DialogDescription>
                  </DialogHeader>
                </div>
                {inviteResult ? (
                  <div className="space-y-5 bg-white px-6 pb-6">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex items-center gap-2 font-semibold text-emerald-900"><CheckCircle2 className="h-5 w-5" />Invitation ready</div>
                      <p className="mt-1 text-xs leading-5 text-emerald-800">The invitation email was delivered to {inviteResult.admin?.email}. The account will activate after the first successful sign-in.</p>
                    </div>
                    <DialogFooter><Button type="button" className="bg-[#0E5B52] hover:bg-[#0B4A43]" onClick={() => setInviteOpen(false)}>Done</Button></DialogFooter>
                  </div>
                ) : <form onSubmit={submitInvite} className="space-y-5 bg-white px-6 pb-6" noValidate>
                  <FormField label="Full name" error={form.formState.errors.fullName?.message}>
                    <Input autoFocus autoComplete="name" placeholder="e.g. Adaeze Okafor" aria-invalid={!!form.formState.errors.fullName} className="bg-white" {...form.register("fullName")} />
                  </FormField>
                  <FormField label="Work email" error={form.formState.errors.email?.message}>
                    <Input type="email" autoComplete="email" placeholder="name@company.com" aria-invalid={!!form.formState.errors.email} className="bg-white" {...form.register("email")} />
                  </FormField>
                  <FormField label="Operational role" error={form.formState.errors.role?.message}>
                    <Select value={form.watch("role")} onValueChange={(value) => form.setValue("role", value, { shouldValidate: true })}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {roleOptions.map((role) => <SelectItem key={role.value} value={role.value}><span className="font-medium">{role.label}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                    {roleOptions.find((role) => role.value === form.watch("role"))?.description && <p className="mt-2 rounded-lg bg-[#F0F6F1] p-3 text-xs text-[#4B635C]">{roleOptions.find((role) => role.value === form.watch("role")).description}</p>}
                  </FormField>
                  <DialogFooter className="pt-2">
                    <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#0E5B52] hover:bg-[#0B4A43]" disabled={inviteBusy}>
                      {inviteBusy && <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" />}Create invitation
                    </Button>
                  </DialogFooter>
                </form>}
              </DialogContent>
            </Dialog>
          )}
        </>}
      />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Stat label="Total admins" value={metrics.total} icon={Users} />
        <Stat label="Active" value={metrics.active} icon={ShieldCheck} />
        <Stat label="Invited" value={metrics.invited} icon={UserPlus} />
        <Stat label="Disabled" value={metrics.disabled} icon={UserCircle2} />
      </div>

      <div className="card-elev rounded-xl p-3">
        <div className="grid gap-2 lg:grid-cols-[minmax(260px,1fr)_210px_190px]">
          <PageSearchInput value={query} onSearch={(value) => { setQuery(value); resetPagination(); }} placeholder="Search name or email…" />
          <Select value={roleFilter} onValueChange={(value) => { setRoleFilter(value); resetPagination(); }}><SelectTrigger className="bg-white"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="all">All roles</SelectItem>{roleOptions.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent></Select>
          <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); resetPagination(); }}><SelectTrigger className="bg-white"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      <div className="card-elev overflow-hidden rounded-xl">
        {loading ? <StateMessage icon={LoaderCircle} text="Loading admin directory…" spin /> :
          error ? <StateMessage text={error} /> :
          !admins.length ? <StateMessage text="No admin users match these filters." /> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-[#F0F6F1]/60"><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last login</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>{admins.map((item) => {
                  const draft = drafts[item.id] || { role: item.role, status: item.status };
                  const locked = item.isProtected || !canManage || item.id === currentAdmin?.id;
                  const dirty = draft.role !== item.role || draft.status !== item.status;
                  return (
                    <TableRow key={item.id}>
                      <TableCell><div className="flex min-w-[240px] items-center gap-3"><Avatar><AvatarImage src={item.avatar || undefined} /><AvatarFallback className="bg-[#0E5B52] text-white">{(item.fullName || item.email).split(" ").map((part) => part[0]).join("").slice(0, 2)}</AvatarFallback></Avatar><div><div className="flex items-center gap-2 font-semibold text-[#064E3B]">{item.fullName}{item.isProtected && <Badge className="border-amber-200 bg-amber-50 text-amber-800"><LockKeyhole className="mr-1 h-3 w-3" />Platform owner</Badge>}</div><div className="text-xs text-[#66736F]">{item.email}</div></div></div></TableCell>
                      <TableCell><Select disabled={locked} value={draft.role} onValueChange={(role) => setDrafts((previous) => ({ ...previous, [item.id]: { ...draft, role } }))}><SelectTrigger className="w-[190px] bg-white"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{roleOptions.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent></Select></TableCell>
                      <TableCell><div className="flex items-center gap-2"><Select disabled={locked} value={draft.status} onValueChange={(status) => setDrafts((previous) => ({ ...previous, [item.id]: { ...draft, status } }))}><SelectTrigger className="w-[130px] bg-white"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{STATUSES.filter((status) => item.status === "Invited" || status !== "Invited").map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent></Select><Badge variant="outline" className={statusTone(item.status)}>{item.status}</Badge></div></TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-[#59655F]">{item.lastLoginAt ? timeAgo(item.lastLoginAt) : "Never"}</TableCell>
                      <TableCell className="text-right"><Button size="sm" disabled={locked || !dirty || savingId === item.id} onClick={() => saveRow(item)} className="bg-[#0E5B52] hover:bg-[#0B4A43]">{savingId === item.id ? <LoaderCircle className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Save</Button></TableCell>
                    </TableRow>
                  );
                })}</TableBody>
              </Table>
            </div>
          )}
        <div className="flex flex-col gap-3 border-t border-[#0E5B52]/10 bg-[#FAFCFB] px-4 py-3 text-xs text-[#66736F] sm:flex-row sm:items-center sm:justify-between">
          <span>Showing {start}–{end} of {total} · up to {PAGE_SIZE} per page</span>
          <div className="flex gap-2"><Button size="sm" variant="outline" disabled={pageIndex === 0 || loading} onClick={() => setPageIndex((value) => value - 1)}><ChevronLeft className="mr-1 h-4 w-4" />Previous</Button><Button size="sm" variant="outline" disabled={!nextCursor || loading} onClick={goNext}>Next<ChevronRight className="ml-1 h-4 w-4" /></Button></div>
        </div>
      </div>
    </div>
  );
}

function FormField({ children, error, label }) {
  return <div><Label className="mb-2 block text-xs font-semibold text-[#344A43]">{label}</Label>{children}{error && <p role="alert" className="mt-1.5 text-xs text-rose-700">{error}</p>}</div>;
}

function Stat({ icon: Icon, label, value }) {
  return <div className="card-elev rounded-xl p-4"><div className="text-[10px] uppercase tracking-[.12em] text-[#6B7280]">{label}</div><div className="mt-2 flex items-center gap-2"><Icon className="h-4 w-4 text-[#0E5B52]" /><span className="font-display text-2xl font-semibold text-[#064E3B]">{value || 0}</span></div></div>;
}

function StateMessage({ icon: Icon, spin, text }) {
  return <div className="flex min-h-44 items-center justify-center px-6 text-center text-sm text-[#59655F]">{Icon && <Icon className={`mr-2 h-5 w-5 ${spin ? "animate-spin" : ""}`} />}{text}</div>;
}
